import { FC, Fragment, useCallback, useMemo, useState, useTransition } from 'react';
import { DecimalFormat } from '@tempusfinance/decimal';
import SidebarWrapper from '../../SidebarWrapper';

import './WalletPopup.scss';
import Icon from '../../Icon';
import Logo from '../../Logo';
import { LogoType } from '../../Logo/LogoType';
import Typography from '../../Typography';
import { shortenAddress } from '../../../../utils';
import {
  addToast,
  walletAddressAtom,
  walletAtom,
  walletTokenBalancesAtom,
  avnuSupportedTokensAtom,
  lendAndBorrowPositionsAtom,
  poolsUserBalanceAtom,
  userNetWorthAtom,
  configAtom,
  activeAccountIdAtom,
  accountsHealthFactorAtom,
} from '../../../../atoms';
import { ButtonWrapper } from '@tempusfinance/common-ui';
import accountsAtom, { addAccount, closeAccount } from '../../../../atoms/lendAndBorrow/accountsAtom';
import { setActiveAccount } from '../../../../atoms/lendAndBorrow/activeAccountIdAtom';
import transactionHistoryAtom, { TransactionHistoryItem } from '../../../../atoms/wallet/transactionHistoryAtom';
import WalletPopupTransaction from './WalletPopupTransaction';
import LendAndBorrowActionHealthFactor from '../../../Products/LendAndBorrow/LendAndBorrowActionHealthFactor';
import { useAtom } from '../../../../lib/state';
import { Decimal } from '../../../../datastructures';
import { useTokenPriceResolver } from '../../../../utils/useTokenPriceResolver';
import { buildAvnuAddressToSymbolMap } from '../../../../utils/tokenPriceResolver';
import { getConfigManager } from '../../../../config';
import { Nullable, Token } from '../../../../interfaces';
import { STABLECOIN_ASSETS, USD_UI_PRECISION } from '../../../../constants';
import { getAccountAddressFromId } from '../../../../utils';

interface WalletPopupProps {
  open: boolean;
  onClose: () => void;
  onDisconnect: () => void;
}

const WalletPopup: FC<WalletPopupProps> = ({ open, onClose, onDisconnect }) => {
  const [, startTransition] = useTransition();

  const [walletAddress] = useAtom(walletAddressAtom);
  const [wallet] = useAtom(walletAtom);
  const [subaccounts] = useAtom(accountsAtom);
  const [transactionHistory] = useAtom(transactionHistoryAtom);
  const [walletTokenBalances] = useAtom(walletTokenBalancesAtom);
  const [avnuSupportedTokens] = useAtom(avnuSupportedTokensAtom);
  const [lendAndBorrowPositions] = useAtom(lendAndBorrowPositionsAtom);
  const [poolsUserBalanceMap] = useAtom(poolsUserBalanceAtom);
  const [userNetWorth] = useAtom(userNetWorthAtom);
  const [config] = useAtom(configAtom);
  const [activeAccountId] = useAtom(activeAccountIdAtom);
  const [healthFactors] = useAtom(accountsHealthFactorAtom);

  const resolvePrice = useTokenPriceResolver();

  const [activeTab, setActiveTab] = useState<'wallet' | 'history'>('wallet');
  const [accountSwitcherOpen, setAccountSwitcherOpen] = useState(false);

  const avnuAddressToSymbol = useMemo(
    () => buildAvnuAddressToSymbolMap(avnuSupportedTokens),
    [avnuSupportedTokens],
  );

  const shortWalletAddress = useMemo(() => {
    if (!walletAddress) {
      return null;
    }

    return shortenAddress(walletAddress);
  }, [walletAddress]);

  const walletLogo = useMemo(() => {
    if (!wallet) {
      return null;
    }

    switch (wallet?.id) {
      case 'argentX':
        return 'wallet-argent';
      case 'braavos':
        return 'wallet-braavos';
      default:
        return null;
    }
  }, [wallet]);

  const sortedWalletBalances = useMemo(
    () =>
      Object.entries(walletTokenBalances)
        .filter(([, balance]) => balance.gt(0))
        .map(([token, balance]) => {
          let tokenTicker: Nullable<string> =
            getConfigManager().getTokenByAddress(token) ?? getConfigManager().getPoolTokenTickerFromAddress(token);

          if (!tokenTicker) {
            tokenTicker = avnuAddressToSymbol[token] ?? null;
          }

          const price = resolvePrice(token, tokenTicker ?? undefined) ?? Decimal.ZERO;

          if (!tokenTicker) {
            return { address: token, token: tokenTicker, price: Decimal.ZERO, balance, value: Decimal.ZERO };
          }

          return { address: token, token: tokenTicker, price, balance, value: price.mul(balance) };
        })
        .sort((a, b) => (b.value.gt(a.value) ? 1 : b.value.lt(a.value) ? -1 : 0)),
    [avnuAddressToSymbol, resolvePrice, walletTokenBalances],
  );

  // Lend & Borrow: show only the active account's positions
  const allLendAndBorrowPositions = useMemo(() => {
    if (!lendAndBorrowPositions || !walletAddress) {
      return [];
    }

    const accountAddress = getAccountAddressFromId(walletAddress, activeAccountId);
    return (lendAndBorrowPositions[accountAddress] ?? [])
      .sort((a, b) => (b.lendBalanceUsd.gt(a.lendBalanceUsd) ? 1 : b.lendBalanceUsd.lt(a.lendBalanceUsd) ? -1 : 0));
  }, [lendAndBorrowPositions, walletAddress, activeAccountId]);

  const lendAndBorrowNetValueFormatted = useMemo(() => {
    if (!allLendAndBorrowPositions.length) return '';
    let lendTotal = Decimal.ZERO;
    let debtTotal = Decimal.ZERO;
    allLendAndBorrowPositions.forEach(position => {
      lendTotal = lendTotal.add(position.lendBalanceUsd);
      debtTotal = debtTotal.add(position.debtBalanceUsd);
    });
    const netValue = lendTotal.sub(debtTotal);
    return DecimalFormat.format(netValue, {
      style: 'currency',
      currency: '$',
      fractionDigits: 2,
      pad: true,
    });
  }, [allLendAndBorrowPositions]);

  // Pools: sorted positions with non-zero balances
  const sortedPoolPositions = useMemo(
    () =>
      Object.entries(poolsUserBalanceMap)
        .filter(([, balance]) => balance.gt(0))
        .sort(([, a], [, b]) => (b.gt(a) ? 1 : b.lt(a) ? -1 : 0)),
    [poolsUserBalanceMap],
  );

  const poolsValueFormatted = useMemo(() => {
    if (!userNetWorth) return '';
    return DecimalFormat.format(userNetWorth.pools, {
      style: 'currency',
      currency: '$',
      fractionDigits: 2,
      pad: true,
    });
  }, [userNetWorth]);

  const toggleAccountSwitcher = useCallback(() => {
    setAccountSwitcherOpen(open => !open);
  }, []);

  const onSwitchAccount = useCallback(
    (accountId: number) => {
      startTransition(() => {
        if (walletAddress) {
          setActiveAccount(walletAddress, accountId);
          setAccountSwitcherOpen(false);
        }
      });
    },
    [walletAddress],
  );

  const onDeleteAccount = useCallback(
    (accountId: number) => {
      startTransition(() => {
        if (walletAddress) {
          closeAccount(walletAddress, accountId);
        }
      });
    },
    [walletAddress],
  );

  const onAddNewAccount = useCallback(() => {
    if (walletAddress) {
      const currentCount = subaccounts.filter(Boolean).length;
      addAccount(walletAddress);
      // Switch to the newly created account (its index is the previous count)
      startTransition(() => {
        setActiveAccount(walletAddress, currentCount);
      });
    }
  }, [walletAddress, subaccounts]);

  const onCopyAddress = useCallback(() => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress);

      addToast({
        id: 'wallet-address-copied',
        message: 'Wallet address copied to clipboard',
        type: 'success',
      });
    }
  }, [walletAddress]);

  const transactionHistoryGroupedByDate = useMemo(() => {
    const grouped: Record<string, TransactionHistoryItem[]> = {};

    transactionHistory.forEach(transaction => {
      const date = new Date(transaction.timestamp);
      const dateString = date.toDateString();

      if (!grouped[dateString]) {
        grouped[dateString] = [];
      }

      grouped[dateString].push(transaction);
    });

    return grouped;
  }, [transactionHistory]);

  return (
    <SidebarWrapper open={open} onClose={onClose}>
      <div className="nostra__walletPopup">
        <div className="nostra__walletPopup__header">
          <div className="nostra__walletPopup__headerTop">
            <div className="nostra__walletPopup__headerInfo">
              <Logo type="token-STRK" size={32} />
              <div className="nostra__walletPopup__headerText">
                <Typography variant="heading-sm" weight="semi-bold" color="text-primary-darkbg">
                  {shortWalletAddress}
                </Typography>
                <div className="nostra__walletPopup__walletType">
                  {walletLogo && (
                    <>
                      <div className="nostra__walletPopup__walletLogo">
                        <Logo type={walletLogo} size={10} />
                      </div>
                      <Typography variant="body-sm" color="text-supporting-darkbg">
                        {walletLogo === 'wallet-argent' ? 'Argent' : 'Braavos'}
                      </Typography>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="nostra__walletPopup__headerActions">
              <ButtonWrapper className="nostra__walletPopup__headerAction" onClick={onCopyAddress}>
                <Icon variant="copy" color="--icon-default-darkbg" size={16} />
              </ButtonWrapper>
              <ButtonWrapper className="nostra__walletPopup__headerAction" onClick={onDisconnect}>
                <Icon variant="exit" color="--icon-default-darkbg" size={16} />
              </ButtonWrapper>
            </div>
          </div>
          <div className="nostra__walletPopup__accountSwitcher">
            <ButtonWrapper className="nostra__walletPopup__accountToggle" onClick={toggleAccountSwitcher}>
              <Typography variant="body-sm" weight="semi-bold" color="text-primary-darkbg">
                {activeAccountId === 0 ? 'Main Account' : `Account ${activeAccountId}`}
              </Typography>
              <LendAndBorrowActionHealthFactor
                healthFactor={healthFactors[`${walletAddress}-${activeAccountId}`]?.value}
                showDot
                size="normal"
              />
              <Icon variant={accountSwitcherOpen ? 'up-chevron' : 'down-chevron'} color="--icon-default-darkbg" size={12} />
            </ButtonWrapper>
          </div>
          {accountSwitcherOpen && (
            <div className="nostra__walletPopup__accountList">
              {subaccounts.map((subaccount, index) => {
                if (!subaccount) return null;
                const isActive = activeAccountId === index;
                return (
                  <div key={index} className="nostra__walletPopup__accountItem" data-active={isActive}>
                    <ButtonWrapper className="nostra__walletPopup__accountSelect" onClick={() => onSwitchAccount(index)}>
                      <Typography variant="body-sm" weight={isActive ? 'semi-bold' : 'regular'} color="text-primary-darkbg">
                        {index === 0 ? 'Main Account' : subaccount}
                      </Typography>
                      <LendAndBorrowActionHealthFactor
                        healthFactor={healthFactors[`${walletAddress}-${index}`]?.value}
                        showDot
                        size="normal"
                      />
                    </ButtonWrapper>
                    {subaccounts.filter(Boolean).length > 1 && (
                      <ButtonWrapper onClick={() => onDeleteAccount(index)}>
                        <Icon variant="delete" size={12} color="--icon-default-darkbg" />
                      </ButtonWrapper>
                    )}
                  </div>
                );
              })}
              <ButtonWrapper className="nostra__walletPopup__addAccount" onClick={onAddNewAccount}>
                <Icon variant="plus" size={14} color="--icon-default-darkbg" />
                <Typography variant="body-sm" color="text-supporting-darkbg">
                  Add account
                </Typography>
              </ButtonWrapper>
            </div>
          )}
        </div>
        <div className="nostra__walletPopup__tabs">
          <ButtonWrapper
            className="nostra__walletPopup__tab"
            onClick={() => setActiveTab('wallet')}
            data-active={activeTab === 'wallet'}
          >
            <Typography variant="body-sm" weight="semi-bold">
              Wallet
            </Typography>
          </ButtonWrapper>
          <ButtonWrapper
            className="nostra__walletPopup__tab"
            onClick={() => setActiveTab('history')}
            data-active={activeTab === 'history'}
          >
            <Typography variant="body-sm" weight="semi-bold">
              History
            </Typography>
          </ButtonWrapper>
        </div>
        <div className="nostra__walletPopup__tabContent">
          {activeTab === 'wallet' && (
            <div className="nostra__walletPopup__walletBalances">
              {/* Lend & Borrow positions */}
              <div className="nostra__walletPopup__sectionLabel">
                <Typography variant="body-sm" weight="semi-bold" color="text-supporting">
                  Lend & Borrow
                </Typography>
                {lendAndBorrowNetValueFormatted && (
                  <Typography variant="body-sm" color="text-supporting">
                    {lendAndBorrowNetValueFormatted}
                  </Typography>
                )}
              </div>
              {allLendAndBorrowPositions.length > 0 ? (
                allLendAndBorrowPositions.map(position => {
                  const lendFormatted = position.lendBalanceUsd.isZero()
                    ? ''
                    : DecimalFormat.format(position.lendBalanceUsd, {
                        style: 'currency',
                        currency: '$',
                        fractionDigits: 2,
                        pad: true,
                      });
                  const debtFormatted = position.debtBalanceUsd.isZero()
                    ? ''
                    : DecimalFormat.format(position.debtBalanceUsd, {
                        style: 'currency',
                        currency: '$',
                        fractionDigits: 2,
                        pad: true,
                      });

                  return (
                    <div key={`${position.asset.token}-${position.accountId}`} className="nostra__walletPopup__balanceRow">
                      <div className="nostra__walletPopup__balanceAsset">
                        <Logo type={`token-${position.asset.token}`} size={20} />
                        <Typography variant="body-md" weight="semi-bold">
                          {position.asset.token}
                        </Typography>
                      </div>
                      <div className="nostra__walletPopup__balanceValues">
                        {lendFormatted && (
                          <div className="nostra__walletPopup__balanceValueRow">
                            <Typography variant="body-sm" color="text-supporting">
                              Supply
                            </Typography>
                            <Typography variant="body-sm" weight="semi-bold">
                              {lendFormatted}
                            </Typography>
                          </div>
                        )}
                        {debtFormatted && (
                          <div className="nostra__walletPopup__balanceValueRow">
                            <Typography variant="body-sm" color="text-supporting">
                              Debt
                            </Typography>
                            <Typography variant="body-sm" weight="semi-bold">
                              {debtFormatted}
                            </Typography>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="nostra__walletPopup__sectionEmpty">
                  <Typography variant="body-sm" color="text-supporting">
                    No positions
                  </Typography>
                </div>
              )}
              {/* Pool positions */}
              {sortedPoolPositions.length > 0 && (
                <>
                  <div className="nostra__walletPopup__sectionLabel">
                    <Typography variant="body-sm" weight="semi-bold" color="text-supporting">
                      Pools
                    </Typography>
                    {poolsValueFormatted && (
                      <Typography variant="body-sm" color="text-supporting">
                        {poolsValueFormatted}
                      </Typography>
                    )}
                  </div>
                  {sortedPoolPositions.map(([pairId, balance]) => {
                    const poolPair = config.pools[pairId]?.pair;
                    if (!poolPair) return null;

                    const balanceFormatted = DecimalFormat.format(balance, {
                      style: 'currency',
                      currency: '$',
                      fractionDigits: 2,
                      pad: true,
                    });

                    return (
                      <div key={pairId} className="nostra__walletPopup__balanceRow">
                        <div className="nostra__walletPopup__balanceAsset">
                          <div className="nostra__walletPopup__poolLogos">
                            <Logo type={`token-${poolPair.tokenA.ticker}`} size={20} />
                            <Logo type={`token-${poolPair.tokenB.ticker}`} size={20} />
                          </div>
                          <Typography variant="body-md" weight="semi-bold">
                            {`${poolPair.tokenA.ticker}-${poolPair.tokenB.ticker}`}
                          </Typography>
                        </div>
                        <div className="nostra__walletPopup__balanceValues">
                          <Typography variant="body-md" weight="semi-bold">
                            {balanceFormatted}
                          </Typography>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
              {/* Token balances */}
              {sortedWalletBalances.length > 0 && (
                <>
                  <div className="nostra__walletPopup__sectionLabel">
                    <Typography variant="body-sm" weight="semi-bold" color="text-supporting">
                      Tokens
                    </Typography>
                  </div>
                  {sortedWalletBalances.map(({ address, token, price, balance }) => {
                    if (!token) {
                      return null;
                    }

                    const usdDecimals =
                      getConfigManager().getConfig().assets[token as Token]?.fiatTokenPrecision ??
                      (STABLECOIN_ASSETS.includes(token) ? 4 : USD_UI_PRECISION);

                    let tokenDecimals = getConfigManager().getConfig().assets[token as Token]?.uiTokenPrecision;
                    if (!tokenDecimals) {
                      Object.values(getConfigManager().getConfig().pools).forEach(pool => {
                        if (tokenDecimals) return;
                        if (pool.pair.tokenA.ticker === token) tokenDecimals = pool.pair.tokenA.uiDecimals;
                        if (pool.pair.tokenB.ticker === token) tokenDecimals = pool.pair.tokenB.uiDecimals;
                      });
                    }

                    const amountFormatted = DecimalFormat.format(balance, {
                      style: 'decimal',
                      fractionDigits: tokenDecimals ?? 4,
                      pad: true,
                      lessThanFormat: true,
                    });

                    const valueFormatted = DecimalFormat.format(price.mul(balance), {
                      style: 'currency',
                      currency: '$',
                      fractionDigits: usdDecimals,
                      pad: true,
                      lessThanFormat: true,
                    });

                    return (
                      <div key={address} className="nostra__walletPopup__balanceRow">
                        <div className="nostra__walletPopup__balanceAsset">
                          <Logo type={`token-${token === 'wstETH' ? 'wstETH-legacy' : token}` as LogoType} size={20} />
                          <Typography variant="body-md" weight="semi-bold">
                            {token === 'wstETH' ? 'wstETH (Legacy)' : token}
                          </Typography>
                        </div>
                        <div className="nostra__walletPopup__balanceValues">
                          <Typography variant="body-md" weight="semi-bold">
                            {amountFormatted}
                          </Typography>
                          <Typography variant="body-sm" color="text-supporting">
                            {valueFormatted}
                          </Typography>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
          {activeTab === 'history' && (
            <div className="nostra__walletPopup__transactionHistory">
              {Object.entries(transactionHistoryGroupedByDate).length > 0 ? (
                Object.entries(transactionHistoryGroupedByDate).map(([date, transactions]) => {
                  return (
                    <Fragment key={date}>
                      <Typography
                        className="nostra__walletPopup__transactionDate"
                        variant="table-body-md"
                        weight="semi-bold"
                        color="text-supporting"
                      >
                        {date}
                      </Typography>
                      {transactions.map((transaction, index) => {
                        return <WalletPopupTransaction key={`${transaction.txHash ?? index}-${transaction.type}`} transaction={transaction} />;
                      })}
                    </Fragment>
                  );
                })
              ) : (
                <div className="nostra__walletPopup__balancesEmpty">
                  <Typography variant="body-sm" color="text-supporting">
                    No transactions yet
                  </Typography>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </SidebarWrapper>
  );
};
export default WalletPopup;
